import { Injectable, OnDestroy } from '@angular/core';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, Subscription } from 'rxjs';
import {
  AddingModeEnum,
  Expression,
  Expressions,
  LegacyFreeFormFilterExpression,
  Operator
} from '../../interfaces';
import { AdvancedSearchCommService } from '../advanced-search-comm/advanced-search-comm.service';
import { AdvancedSearchQueryBuilderService } from '../advanced-search-query-builder/advanced-search-query-builder.service';
import { ExpressionStructure, KeywordsMatches, keysMapping } from './types';

@Injectable({
  providedIn: 'root'
})
/**
 * Service used from outside this module
 */
// This does not work with grouping
export class AdvancedSearchService implements OnDestroy {
  public addFilters$ = new BehaviorSubject<{
    filters: Expression[];
    clearExistingFilters: boolean;
    autoUpdate: boolean;
  }>(null);
  public isLoading$ = new BehaviorSubject<boolean>(false);
  public submitClicked$ = new BehaviorSubject<boolean>(false);
  public filtersAutoApplied$ = new BehaviorSubject<boolean>(null);
  public addingMode$ = new BehaviorSubject<AddingModeEnum>(null);
  public cancelingLoad$ = new BehaviorSubject<boolean>(null);
  private _expressions: Expressions = { withIds: null, withValues: null };
  private _expressionStructure: ExpressionStructure = { keywords: [], values: [] };
  private _subscriptions = {
    expression: new Subscription(),
    submitClicked: new Subscription()
  };

  public get expressions(): Expressions {
    return this._expressions;
  }

  public get expressionStructure(): ExpressionStructure {
    return cloneDeep(this._expressionStructure);
  }

  public set isLoading(isLoading: boolean) {
    this.isLoading$.next(isLoading);
  }

  constructor(
    private _advancedSearchQueryBuilderService: AdvancedSearchQueryBuilderService,
    private _advancedSearchCommService: AdvancedSearchCommService
  ) {
    this._registerSubscriptions();
  }

  ngOnDestroy() {
    this._subscriptions.expression.unsubscribe();
    this.submitClicked$.complete();
  }

  public unsubscribe(): void {
    this.submitClicked$.complete();
    this._subscriptions.expression.unsubscribe();
    this._subscriptions.submitClicked.unsubscribe();
  }

  public cancelingLoad(): void {
    this.cancelingLoad$.next(true);
  }

  public addingMode(addingMode: AddingModeEnum): void {
    this.addingMode$.next(addingMode);
  }

  public filtersAutoApply(): void {
    this.filtersAutoApplied$.next(true);
  }

  public submitClicked(): void {
    this.submitClicked$.next(true);
  }

  public addFilters(
    filters: Expression[],
    clearExistingFilters: boolean = false,
    autoUpdate: boolean = false
  ): void {
    this.addFilters$.next({ filters, clearExistingFilters, autoUpdate });
  }

  /**
   * Update keywords expression with resulting prepared for backend expression
   * @param {string} query - query expression
   * @param {KeywordsMatches} keys - used to replace the placeholder keys
   */
  public updateExpressionKeys(query: string, keys: KeywordsMatches = keysMapping): string {
    if (!query) {
      return '';
    }

    Object.keys(keys).forEach(key => {
      query = query.replace(`{${key}}`, keys[key]);
    });

    return query;
  }

  public extractExpressionStructure(
    query: string = this._expressions.withIds
  ): ExpressionStructure {
    const keywordsValuesPairs = this._advancedSearchQueryBuilderService.extractKeysValuesPairs(
      query
    );
    const keywords = [];
    const values = [];

    keywordsValuesPairs.forEach(pair => {
      keywords.push(pair[0]);
      values.push(pair[1]);
    });

    return {
      keywords,
      values
    };
  }

  public extractKeywordsValuesPairs(queryExpression: string): string[][] {
    return this._advancedSearchQueryBuilderService.extractKeysValuesPairs(queryExpression);
  }

  public extractKeywordsValuesObjects(queryExpression: string): Expression[] {
    return this._advancedSearchQueryBuilderService
      .extractKeysValuesPairs(queryExpression)
      .map(filter => {
        return {
          keyword: filter[0],
          value: filter[1],
          operator: Operator[filter[2]]
        };
      });
  }

  public includesKey(keyword: string, query: string = this._expressions.withIds): boolean {
    return this.extractExpressionStructure(query).keywords.includes(keyword);
  }

  public countKey(keyword: string, query: string = this._expressions.withIds): number {
    return this.extractExpressionStructure(query).keywords.filter(key => key === keyword).length;
  }

  public getValueForKey(keyword: string, query: string = this._expressions.withIds): string {
    let value: string = null;

    this._advancedSearchQueryBuilderService.extractKeysValuesPairs(query).forEach(keyValuePair => {
      if (keyValuePair[0] === keyword) {
        value = keyValuePair[1];
      }
    });

    return value;
  }

  public convertLegacyUrlFiltersToExpression(expression: LegacyFreeFormFilterExpression[]): string {
    let query = '';

    expression.forEach(block => {
      if (block.logicalOp) {
        query += ` ${block.logicalOp} `;
      }
      query += `{${this._getKeywordForLegacyKeyword(block.keyword)}}${block.operator}${
        block.operands[0]
      }`;
    });

    return query;
  }

  private _getKeywordForLegacyKeyword(keyword: string) {
    switch (keyword) {
      case 'site':
        return 'site1';
    }

    return keyword;
  }

  /**
   * Register subscriptions
   */
  private _registerSubscriptions(): void {
    this._subscriptions.expression = this._advancedSearchCommService.expression$.subscribe(
      expressions => {
        this._expressions = expressions;
        this._expressionStructure = this.extractExpressionStructure(this._expressions.withIds);
      }
    );
  }
}
